import { create } from 'zustand';
import { tutorsApi, repositoriesApi } from '../services/api';

export const useTutorsStore = create((set, get) => ({
  // State
  tutors: [],
  aiTutors: [],
  selectedTutor: null,
  conversations: [],
  currentConversation: null,
  chatHistory: [],
  repositories: [],
  sandboxInstances: [],
  loading: false,
  error: null,

  // Legacy tutors
  fetchTutors: async (token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.getTutors(token);
      set({
        tutors: response || [],
        loading: false,
      });
      return response;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  fetchTutorById: async (tutorId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.getTutor(tutorId, token);
      set({
        selectedTutor: response,
        loading: false,
      });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createTutor: async (tutorData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.createTutor(tutorData, token);

      // Update tutors list
      const tutors = await tutorsApi.getTutors(token);
      set({
        tutors: tutors,
        loading: false,
      });

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateTutor: async (tutorId, tutorData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.updateTutor(tutorId, tutorData, token);

      // Update tutor in lists
      set((state) => ({
        tutors: state.tutors.map((t) =>
          t.id === tutorId ? response : t
        ),
        selectedTutor:
          state.selectedTutor?.id === tutorId
            ? response
            : state.selectedTutor,
        loading: false,
      }));

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteTutor: async (tutorId, token) => {
    set({ loading: true, error: null });
    try {
      await tutorsApi.deleteTutor(tutorId, token);

      // Remove tutor from lists
      set((state) => ({
        tutors: state.tutors.filter((t) => t.id !== tutorId),
        selectedTutor:
          state.selectedTutor?.id === tutorId ? null : state.selectedTutor,
        loading: false,
      }));

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // AI Tutors
  fetchAITutors: async (type, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.getAITutors(type, token);
      set({
        aiTutors: response || [],
        loading: false,
      });
      return response;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  createAITutor: async (tutorData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.createAITutor(tutorData, token);

      // Update AI tutors list
      const tutors = await tutorsApi.getAITutors('owned', token);
      set({
        aiTutors: tutors,
        loading: false,
      });

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteAITutor: async (tutorId, token) => {
    set({ loading: true, error: null });
    try {
      await tutorsApi.deleteAITutor(tutorId, token);

      // Remove tutor from lists
      set((state) => ({
        aiTutors: state.aiTutors.filter((t) => t.id !== tutorId),
        selectedTutor:
          state.selectedTutor?.id === tutorId ? null : state.selectedTutor,
        loading: false,
      }));

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Conversations
  fetchConversations: async (token) => {
    try {
      const response = await tutorsApi.getConversations(token);
      set({ conversations: response });
      return response;
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      throw error;
    }
  },

  createConversation: async (title, tutorId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.createConversation(title, tutorId, token);

      // Update conversations list
      const conversations = await tutorsApi.getConversations(token);
      set({
        conversations: conversations,
        currentConversation: response,
        loading: false,
      });

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchChatHistory: async (conversationId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.getChatHistory(conversationId, token);
      set({
        chatHistory: response || [],
        loading: false,
      });
      return response;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  sendMessage: async (tutorId, data, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.sendMessage(tutorId, data, token);

      // Append new message to chat history
      set((state) => ({
        chatHistory: [...state.chatHistory, response],
        loading: false,
      }));

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Materials
  uploadMaterial: async (tutorId, file, token) => {
    set({ loading: true, error: null });
    try {
      const response = await tutorsApi.uploadMaterial(tutorId, file, token);
      set({ loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Repositories
  fetchRepositories: async (token) => {
    try {
      const response = await repositoriesApi.getMaterialRepositories(token);
      set({ repositories: response });
      return response;
    } catch (error) {
      console.error('Failed to fetch repositories:', error);
      throw error;
    }
  },

  createRepository: async (repoData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await repositoriesApi.createMaterialRepository(
        repoData,
        token
      );

      // Update repositories list
      const reposResponse = await repositoriesApi.getMaterialRepositories(token);
      set({
        repositories: reposResponse,
        loading: false,
      });

      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteRepository: async (repoId, token) => {
    set({ loading: true, error: null });
    try {
      await repositoriesApi.deleteMaterialRepository(repoId, token);

      // Remove repository from list
      set((state) => ({
        repositories: state.repositories.filter((r) => r.id !== repoId),
        loading: false,
      }));

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  linkRepository: async (tutorId, repoId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await repositoriesApi.linkRepositoryToTutor(
        tutorId,
        repoId,
        token
      );
      set({ loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  unlinkRepository: async (tutorId, repoId, token) => {
    set({ loading: true, error: null });
    try {
      const response = await repositoriesApi.unlinkRepositoryFromTutor(
        tutorId,
        repoId,
        token
      );
      set({ loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  uploadRepositoryMaterial: async (repoId, fileData, token) => {
    set({ loading: true, error: null });
    try {
      const response = await repositoriesApi.uploadRepositoryMaterial(
        repoId,
        fileData,
        token
      );
      set({ loading: false });
      return response;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Sandbox instances
  fetchSandboxInstances: async (token) => {
    try {
      const response = await tutorsApi.getSandboxInstances(token);
      set({ sandboxInstances: response });
      return response;
    } catch (error) {
      console.error('Failed to fetch sandbox instances:', error);
      throw error;
    }
  },

  setCurrentConversation: (conversation) => {
    set({ currentConversation: conversation });
  },

  selectTutor: (tutorId) => {
    const tutor =
      get().tutors.find((t) => t.id === tutorId) ||
      get().aiTutors.find((t) => t.id === tutorId);
    set({ selectedTutor: tutor });
  },

  clearSelectedTutor: () => set({ selectedTutor: null }),

  clearChatHistory: () => set({ chatHistory: [] }),

  clearError: () => set({ error: null }),
}));
